<template>
  <div>
    <div
      class="o-mobileMenu o-flex -column -spaceBetween t-body2"
      data-cy="MobileMenu"
      :inert="!isMenuOpen ? '' : null"
      :aria-hidden="!isMenuOpen ? 'true' : null"
      :class="{ '-open': isMenuOpen }">
      <div class="u-spacer" />
      <div
        data-cy="MobileMenu__content">
        <div
          v-if="communityNavEntries.length"
          class="t-h3 t-fontWeightSemibold u-marginLeft5 u-marginBottom4">
          {{ $t('nav.communities') }}
        </div>
        <template v-for="entry in communityNavEntries">
          <router-link
            :key="`community-${entry.route.params.id}`"
            v-slot="{ href, navigate }"
            :to="entry.route">
            <a
              class="o-mobileMenu__link"
              :href="href"
              @click="goAndClose($event, navigate)">
              <template v-if="entry.iconType === 'fontawesome'">
                <FontAwesomeIcon
                  :icon="entry.icon"
                  :size="20"
                  class="o-mobileMenu__fontawesomeIcon"
                  data-cy="MobileMenu__entryIcon" />
              </template>
              <template v-else>
                <SvgIcon
                  :type="`menu_outline_${entry.icon}`"
                  data-cy="MobileMenu__entryIcon" />
              </template>
              {{ entry.label }}
            </a>
          </router-link>
        </template>
        <hr
          v-if="communityNavEntries.length"
          class="o-mobileMenu__divider" />
        <div
          class="t-h3 t-fontWeightSemibold u-marginLeft5 u-marginBottom4"
          data-cy="MobileMenu__title">
          {{ $t('nav.more') }}
        </div>
        <template v-for="(entry, index) in menuEntries">
          <hr
            v-if="entry.divider"
            :key="`divider-${index}`"
            class="o-mobileMenu__divider"
            data-cy="MobileMenu__divider" />
          <router-link
            v-else
            :key="`${entry.label}-route`"
            v-slot="{ href, navigate }"
            :to="entry.route"
            data-cy="MobileMenu__entry">
            <a
              class="o-mobileMenu__link"
              :href="href"
              @click="goAndClose($event, navigate)">
              <SvgIcon
                :type="`menu_outline_${entry.icon}`"
                data-cy="MobileMenu__entryIcon" />
              {{ entry.label }}
            </a>
          </router-link>
        </template>
        <hr
          class="o-mobileMenu__divider"
          data-cy="MobileMenu__divider" />
        <LanguageSelector
          upwards
          data-cy="MobileMenu__languageSelector">
          <template #default="language">
            <div class="o-mobileMenu__link t-caption1 t-fontWeightMedium u-marginBottom0 u-marginTop0">
              <SvgIcon type="language" />
              {{ language.label }}
              <SvgIcon
                type="arrow_dropdown"
                class="s-languageDropdownIcon u-marginRight0 t-caption1" />
            </div>
          </template>
        </LanguageSelector>
        <router-link
          v-slot="{ href, navigate }"
          :to="{
            name: 'profile.profile',
          }">
          <a
            class="o-mobileMenu__link"
            data-cy="MobileMenu__openProfile"
            :href="href"
            @click="goAndClose($event, navigate)">
            <SvgIcon
              type="menu_outline_user"
              data-cy="MobileMenu__profileIcon" />
            {{ $t('pages.my_profile') }}
          </a>
        </router-link>
      </div>
    </div>
    <nav
      class="o-tabBar"
      :aria-label="$t('nav.mobile_navigation')">
      <router-link
        v-for="(tabbarEntry, index) in tabbarEntries"
        :key="`tabbar-${index}`"
        v-slot="{ href, navigate, isExactActive }"
        :to="tabbarEntry.route">
        <a
          class="o-tabBar__link"
          :class="{ '-active': isExactActive && !isMenuOpen }"
          data-cy="MobileMenu__tabBarEntry"
          :href="href"
          @click="goAndClose($event, navigate)">
          <div>
            <SvgIcon
              v-if="isExactActive && !isMenuOpen"
              :type="`menu_filled_${tabbarEntry.icon}`"
              data-cy="MobileMenu__tabBarIconActive" />
            <SvgIcon
              v-else
              :type="`menu_outline_${tabbarEntry.icon}`"
              data-cy="MobileMenu__tabBarIcon" />
          </div>
          <span>{{ tabbarEntry.label }}</span>
        </a>
      </router-link>
      <a
        v-if="navLinks.length > 5"
        class="o-tabBar__link"
        :class="{ '-active': isMenuOpen }"
        data-cy="MobileMenu__tabBarMenu"
        href="#"
        role="button"
        :aria-expanded="String(isMenuOpen)"
        :aria-label="$t('nav.more')"
        @click.prevent="toggleExpandedMenu">
        <div>
          <SvgIcon
            v-if="isMenuOpen"
            :type="`menu_filled_dots`"
            data-cy="MobileMenu__tabBarMenuOpen" />
          <SvgIcon
            v-else
            :type="`menu_outline_dots`"
            data-cy="MobileMenu__tabBarMenuClosed" />
        </div>
        <span>{{ $t('nav.more') }}</span>
      </a>
    </nav>
  </div>
</template>

<script>
import navigationMixin from './navigationMixin'

import FontAwesomeIcon from '@/components/FontAwesomeIcon.vue'
import { useChatStore } from '@/modules/chat/chatStore'

export default {
  mixins: [navigationMixin],
  data() {
    return {
      isMenuOpen: false,
      chatStore: useChatStore(),
    }
  },
  computed: {
    menuEntries() {
      // remove menu entries shown in tabbar
      const menuEntries = this.navigation.filter((entry) => !this.tabbarEntries.some((tabbarEntry) => tabbarEntry.route.name === entry.route?.name))
      // return superfluous dividers
      return menuEntries.filter((entry, index) => {
        if (!entry.divider) {
          return true
        }
        // starting divider
        if (index === 0) {
          return false
        }
        // ending divider
        if (index === menuEntries.length - 1) {
          return false
        }
        // divider following divider
        if (menuEntries[index - 1]?.divider) {
          return false
        }

        return true
      })
    },
    navLinks() {
      // remove divider entries
      return this.navigation.filter((entry) => !entry.divider)
    },
    tabbarEntries() {
      if (this.navLinks.length <= 5) {
        return this.navLinks
      }
      return this.navLinks.slice(0, 4)
    },
  },
  watch: {
    $route() {
      this.isMenuOpen = false
    },
  },
  methods: {
    goAndClose(event, navigate) {
      this.isMenuOpen = false
      navigate(event)
    },
    toggleExpandedMenu() {
      this.isMenuOpen = !this.isMenuOpen
      if (this.isMenuOpen) {
        this.chatStore.closeChatWhenNavigatingInMobile()
      }
    },
  },
  components: {
    FontAwesomeIcon,
  },
}
</script>

<style lang="scss" scoped>
.o-mobileMenu__fontawesomeIcon {
  margin-right: 16px;
}
</style>
